import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Icon } from '../components/Icon.tsx';
import { meetingsApi, type MeetingRow } from '../features/meetings/api.ts';
import { statsApi, type LeaderboardRow } from '../features/stats/api.ts';
import { thanfeezuApi, type DecisionStatus } from '../features/thanfeezu/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { meetingStatus } from '../lib/meetingStatus.ts';
import { displayName } from '../lib/utils.ts';
import { bodyOfRole, type Body } from '../lib/body.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { formatDvDate, formatDvTime } from '../lib/dvDate.ts';

const SUBMITTERS = new Set(['PRESIDENT', 'SECRETARY_GENERAL', 'MEMBER']);

// Shared status pill variant — matches MeetingDetail so a meeting's status reads
// identically across the app.
interface Remaining { total: number; days: number; hours: number; minutes: number; seconds: number }

function remainingTo(iso: string): Remaining {
  const total = Math.max(0, new Date(iso).getTime() - Date.now());
  const secs = Math.floor(total / 1000);
  return {
    total,
    days: Math.floor(secs / 86400),
    hours: Math.floor((secs % 86400) / 3600),
    minutes: Math.floor((secs % 3600) / 60),
    seconds: secs % 60,
  };
}

function CountdownBox({ value, label }: { value: number; label: string }): JSX.Element {
  return (
    <div className="flex w-[4.75rem] flex-col items-center justify-center gap-1 rounded-3xl bg-[hsl(var(--secondary-container))] py-4 text-center text-[hsl(var(--on-secondary-container))] sm:w-24">
      <span key={value} dir="ltr" className="m3-tick tabular block w-full text-center text-4xl font-medium leading-none sm:text-5xl">{String(value).padStart(2, '0')}</span>
      <span className="mt-1 block w-full text-center text-[11px] font-medium opacity-70">{label}</span>
    </div>
  );
}

// A scannable meeting row — meeting number (mono), title, and a meta row with
// icons, reused on the dashboard lists. Mirrors the Meetings page row.
function MeetingListRow({ meeting }: { meeting: MeetingRow }): JSX.Element {
  return (
    <Link
      to="/meetings/$meetingId"
      params={{ meetingId: meeting.id }}
      className="flex items-center gap-4 px-4 sm:px-5 py-3.5 transition-colors hover:bg-[hsl(var(--surface-high))]"
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
        <Icon name="event" size={22} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-snug truncate">
          {meeting.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Icon name="calendar_today" size={15} className="shrink-0 text-foreground/40" />
            {formatDvDate(meeting.scheduledAt)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="schedule" size={15} className="shrink-0 text-foreground/40" />
            <span className="tabular" dir="ltr">{formatDvTime(meeting.scheduledAt)}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="location_on" size={15} className="shrink-0 text-foreground/40" />
            {meeting.location}
          </span>
        </div>
      </div>
      <span className={`${meetingStatus(meeting).pill} shrink-0`}>
        {meetingStatus(meeting).label}
      </span>
    </Link>
  );
}

function Hero({ meeting }: { meeting: MeetingRow }): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [now, setNow] = useState(() => remainingTo(meeting.scheduledAt));

  useEffect(() => {
    setNow(remainingTo(meeting.scheduledAt));
    const id = setInterval(() => setNow(remainingTo(meeting.scheduledAt)), 1000);
    return () => clearInterval(id);
  }, [meeting.scheduledAt]);

  const goToMeeting = (): void => {
    void navigate({ to: '/meetings/$meetingId', params: { meetingId: meeting.id } });
  };
  const canSubmit = meeting.status === 'SCHEDULED' && !!user && SUBMITTERS.has(user.role);

  return (
    <section className="space-y-7 rounded-3xl bg-[hsl(var(--chrome))] p-6 text-foreground lg:p-10">
      {/* Next-meeting masthead — label, title and a clean meta row with icons,
          matching the MeetingDetail header so context scans at a glance. */}
      <div className="space-y-3 text-center">
        <p className="status-label">{dv.dashboard.nextMeeting}</p>
        <h2 className="text-2xl font-semibold leading-tight lg:text-3xl">
          {meeting.title}
        </h2>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Icon name="calendar_today" size={16} className="shrink-0 text-foreground/40" />
            {formatDvDate(meeting.scheduledAt)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="schedule" size={16} className="shrink-0 text-foreground/40" />
            <span className="tabular" dir="ltr">{formatDvTime(meeting.scheduledAt)}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Icon name="location_on" size={16} className="shrink-0 text-foreground/40" />
            {meeting.location}
          </span>
        </div>
      </div>

      <div className="text-center">
        <p className="status-label mb-3">{now.total <= 0 ? '' : dv.dashboard.startsIn}</p>
        {now.total <= 0 ? (
          <p className="text-xl font-semibold">{dv.dashboard.startedNow}</p>
        ) : (
          <div className="flex flex-wrap justify-center gap-3">
            <CountdownBox value={now.days} label={dv.dashboard.days} />
            <CountdownBox value={now.hours} label={dv.dashboard.hours} />
            <CountdownBox value={now.minutes} label={dv.dashboard.minutes} />
            <CountdownBox value={now.seconds} label={dv.dashboard.seconds} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-center gap-3 pt-2">
        {canSubmit && (
          <button onClick={goToMeeting} className="btn-primary">
            {dv.dashboard.submitItemCta}
          </button>
        )}
        <button onClick={goToMeeting} className="btn-secondary">
          {dv.dashboard.openMeeting}
        </button>
      </div>
    </section>
  );
}

const roleLabel = (role: string): string => (dv.roles as Record<string, string>)[role] ?? role;

// Material 3 linear progress indicator: a rounded track, an active indicator
// separated from the remaining track by a small gap, and a stop-dot at the far
// (inline-end) side. `color` is any CSS colour for the indicator.
function M3Bar({ pct, color, thickness }: { pct: number; color: string; thickness: number }): JSX.Element {
  const p = Math.max(0, Math.min(100, pct));
  const GAP = 4; // px, M3 gap between indicator and track
  return (
    <div className="relative w-full" style={{ height: thickness }}>
      <div className="absolute inset-0 rounded-full bg-[hsl(var(--surface-container-highest))]" />
      {p > 0 && (
        <div
          className="absolute inset-y-0 start-0 rounded-full"
          style={{ width: p >= 100 ? '100%' : `calc(${p}% - ${GAP}px)`, background: color }}
        />
      )}
      {p < 100 && (
        <div
          className="absolute end-0 top-1/2 -translate-y-1/2 rounded-full"
          style={{ width: thickness, height: thickness, background: color }}
        />
      )}
    </div>
  );
}

// One leaderboard row — a rank chip, the councilor's name + role, a slim bar
// proportional to their approved-item count, and the approved figure. The #1 row
// gets a subtle accent; the current user's row is highlighted with a "you" badge.
function LbRow({ row, rank, maxApproved, isMe }: {
  row: LeaderboardRow; rank: number; maxApproved: number; isMe: boolean;
}): JSX.Element {
  const pct = maxApproved > 0 ? Math.round((row.approved / maxApproved) * 100) : 0;
  const top = rank === 1;
  return (
    <div className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-colors ${isMe ? 'bg-[hsl(var(--secondary-container))]/70' : 'hover:bg-black/[0.03]'}`}>
      <span
        dir="ltr"
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular ${top ? 'bg-primary text-primary-foreground' : 'bg-[hsl(var(--surface-container-highest))] text-muted-foreground'}`}
      >
        {rank}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{displayName(row)}</span>
          <span className="truncate text-xs text-muted-foreground">{roleLabel(row.role)}</span>
        </div>
        <div className="mt-1.5">
          <M3Bar pct={pct} color={top ? 'hsl(var(--primary))' : 'hsl(var(--primary) / 0.6)'} thickness={6} />
        </div>
      </div>
      <div className="shrink-0 text-end leading-none">
        <span dir="ltr" className="tabular text-xl font-bold">{row.approved}</span>
        <span className="mt-0.5 block text-[10px] text-muted-foreground">{dv.dashboard.leaderboard.masala}</span>
      </div>
    </div>
  );
}

function Leaderboard(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [body, setBody] = useState<Body>(() => (user?.role ? bodyOfRole(user.role) : null) ?? 'COUNCIL');
  const { data } = useQuery({
    queryKey: ['leaderboard', body],
    queryFn: () => statsApi.leaderboard(body),
    enabled: !!user && user.role !== 'SYS_ADMIN',
    // Keep the current board on screen while the other body loads (no flicker).
    placeholderData: keepPreviousData,
  });
  // Warm both bodies once so toggling is instant.
  useEffect(() => {
    if (!user || user.role === 'SYS_ADMIN') return;
    for (const b of ['COUNCIL', 'WDC'] as Body[]) {
      void qc.prefetchQuery({ queryKey: ['leaderboard', b], queryFn: () => statsApi.leaderboard(b) });
    }
  }, [qc, user]);

  if (!data || data.rows.length === 0) return null;

  const rows = data.rows;
  const maxApproved = rows[0]?.approved ?? 0;
  const top = rows.slice(0, 5);
  const myIndex = rows.findIndex((r) => r.userId === user?.id);
  // If the viewer is a councilor ranked outside the top 5, show their own row too.
  const myRow = myIndex >= 5 ? rows[myIndex] : undefined;

  return (
    <section className="rounded-3xl bg-[hsl(var(--chrome))] space-y-4 p-5 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))]">
            <Icon name="emoji_events" size={22} />
          </span>
          <div className="space-y-0.5">
            <h2 className="text-lg font-bold leading-tight">{dv.dashboard.leaderboard.title}</h2>
            <p className="text-xs text-muted-foreground">
              <span dir="ltr" className="tabular">{data.year}</span> · {dv.dashboard.leaderboard.subtitle}
            </p>
          </div>
        </div>
        <BodyToggle body={body} onChange={setBody} />
      </div>
      {maxApproved === 0 ? (
        <p className="py-2 text-center text-sm text-muted-foreground">{dv.dashboard.leaderboard.empty}</p>
      ) : (
        <div className="space-y-1">
          {top.map((r, i) => (
            <LbRow key={r.userId} row={r} rank={i + 1} maxApproved={maxApproved} isMe={r.userId === user?.id} />
          ))}
          {myRow && (
            <>
              <div className="px-3 text-center text-xs text-muted-foreground">···</div>
              <LbRow row={myRow} rank={myIndex + 1} maxApproved={maxApproved} isMe />
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ── Thanfeezu (decision-implementation) infographic card ──────────────────────
// Status colours from the app's printed-stationery palette (teal / forest /
// warm ochre / stone), shared with the thanfeezu page.
const TF_STATUS_ORDER: DecisionStatus[] = ['IMPLEMENTED', 'ONGOING', 'ON_HOLD', 'NOT_STARTED'];
const TF_STATUS_COLOR: Record<DecisionStatus, string> = {
  NOT_STARTED: 'hsl(var(--outline, var(--muted-foreground)))',
  ONGOING: 'hsl(var(--primary))',
  ON_HOLD: 'hsl(var(--warning))',
  IMPLEMENTED: 'hsl(var(--success))',
};

// Implementation-rate ring colour: ≤40% red, 41–75% amber, >75% green.
function tfRateColor(pct: number): string {
  if (pct <= 40) return 'hsl(var(--gauge-low))';
  if (pct <= 75) return 'hsl(var(--gauge-mid))';
  return 'hsl(var(--gauge-high))';
}

// Implementation-rate ring (mirrors the thanfeezu page's seal ring, compact).
function MiniRing({ pct }: { pct: number }): JSX.Element {
  const SIZE = 108; const SW = 10;
  const R = (SIZE - SW) / 2;
  const C = 2 * Math.PI * R;
  const p = Math.max(0, Math.min(100, pct));
  const dash = (p / 100) * C;
  const GAP = 8; // px arc gap between indicator and track (M3 determinate circular)
  const trackLen = Math.max(0, C - dash - 2 * GAP);
  // Rotate each arc to its start angle (avoids dash-offset sign ambiguity): the
  // active arc starts at the top; the remaining track starts just past it + gap.
  const cx = SIZE / 2;
  const trackAngle = -90 + ((dash + GAP) / C) * 360;
  return (
    <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
        {trackLen > 0 && (
          <circle
            cx={cx} cy={cx} r={R} fill="none"
            stroke="hsl(var(--surface-container-highest))" strokeWidth={SW} strokeLinecap="round"
            strokeDasharray={`${trackLen} ${C}`} transform={`rotate(${trackAngle} ${cx} ${cx})`}
          />
        )}
        {dash > 0 && (
          <circle
            cx={cx} cy={cx} r={R} fill="none"
            stroke={tfRateColor(p)} strokeWidth={SW} strokeLinecap="round"
            strokeDasharray={`${dash} ${C}`} transform={`rotate(-90 ${cx} ${cx})`}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span dir="ltr" className="tabular text-2xl font-semibold">{pct}%</span>
        <span className="status-label text-[0.55rem]">{dv.thanfeezu.pctImplemented}</span>
      </div>
    </div>
  );
}

// One labelled status bar — proportion of total decisions in this status.
function TfStatusBar({ label, count, total, color }: { label: string; count: number; total: number; color: string }): JSX.Element {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-24 shrink-0 truncate text-xs text-muted-foreground">{label}</span>
      <div className="flex-1">
        <M3Bar pct={pct} color={color} thickness={8} />
      </div>
      <span dir="ltr" className="tabular w-6 shrink-0 text-end text-sm font-semibold">{count}</span>
    </div>
  );
}

function ThanfeezuCard(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [body, setBody] = useState<Body>(() => (user?.role ? bodyOfRole(user.role) : null) ?? 'COUNCIL');
  const tfQuery = (b: Body) => ({ queryKey: ['thanfeezu', 'summary', b], queryFn: () => thanfeezuApi.list(b).then((r) => r.stats) });
  const { data: stats } = useQuery({
    ...tfQuery(body),
    enabled: !!user && user.role !== 'SYS_ADMIN',
    // Keep the current infographic on screen while the other body loads (no flicker).
    placeholderData: keepPreviousData,
  });
  // Warm both bodies once so toggling is instant.
  useEffect(() => {
    if (!user || user.role === 'SYS_ADMIN') return;
    for (const b of ['COUNCIL', 'WDC'] as Body[]) void qc.prefetchQuery(tfQuery(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, user]);

  if (!stats) return null;

  return (
    <section className="rounded-3xl bg-[hsl(var(--chrome))] space-y-4 p-5 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]">
            <Icon name="fact_check" size={22} />
          </span>
          <div className="space-y-0.5">
            <h2 className="text-lg font-bold leading-tight">{dv.thanfeezu.title}</h2>
            <p className="text-xs text-muted-foreground">{dv.thanfeezu.subtitle}</p>
          </div>
        </div>
        <BodyToggle body={body} onChange={setBody} />
      </div>

      {stats.total === 0 ? (
        <p className="py-2 text-center text-sm text-muted-foreground">{dv.thanfeezu.empty}</p>
      ) : (
        <>
          <div className="flex items-center gap-5">
            <MiniRing pct={stats.pctImplemented} />
            <div className="min-w-0 flex-1 space-y-2.5">
              {TF_STATUS_ORDER.map((st) => (
                <TfStatusBar key={st} label={dv.thanfeezu.status[st]} count={stats.counts[st]} total={stats.total} color={TF_STATUS_COLOR[st]} />
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 border-t border-black/[0.06] pt-3 text-sm">
            <span className="text-muted-foreground">
              {dv.thanfeezu.total} <span dir="ltr" className="tabular font-bold text-foreground">{stats.total}</span>
            </span>
            {stats.avgDaysToImplement != null && (
              <span className="text-muted-foreground">
                {dv.thanfeezu.avgDays} <span dir="ltr" className="tabular font-bold text-foreground">{stats.avgDaysToImplement} {dv.thanfeezu.days}</span>
              </span>
            )}
            {stats.overdue > 0 && (
              <span className="font-medium text-destructive">
                ⚠ {dv.thanfeezu.overdue} <span dir="ltr" className="tabular font-bold">{stats.overdue}</span>
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  // The dashboard hides cancelled meetings — the hero countdown + quick lists
  // are about what's actually happening (the meetings list keeps them on record).
  const { data: upcoming } = useQuery({
    queryKey: ['meetings', 'upcoming', 'active'],
    queryFn: () => meetingsApi.list('upcoming', 1, undefined, undefined, undefined, true),
    enabled: !!user && user.role !== 'SYS_ADMIN',
  });
  const { data: past } = useQuery({
    queryKey: ['meetings', 'past', 'active'],
    queryFn: () => meetingsApi.list('past', 1, undefined, undefined, undefined, true),
    enabled: !!user && user.role !== 'SYS_ADMIN',
  });

  if (user?.role === 'SYS_ADMIN') return <Navigate to="/admin/orgs" />;

  const upcomingList = upcoming?.meetings ?? [];
  const nearest = upcomingList[0];
  const rest = upcomingList.slice(1, 6);
  const pastList = (past?.meetings ?? []).slice(0, 5);

  return (
    <div className="page space-y-6 lg:space-y-8">
      {/* Welcome greeting + the user's designation/role for quiet context. */}
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">{dv.dashboard.welcome}، {user ? displayName(user) : ''}</h1>
        {user && (
          <p className="text-sm text-muted-foreground">{user.designationDv ?? dv.roles[user.role]}</p>
        )}
      </header>

      {/* Countdown hero — the calm centerpiece for the next meeting. */}
      {nearest ? (
        <Hero meeting={nearest} />
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] space-y-2 p-6 text-center lg:p-10">
          <p className="status-label">{dv.dashboard.nextMeeting}</p>
          <p className="text-muted-foreground">{dv.dashboard.noUpcoming}</p>
          <div className="pt-2">
            <Link to="/meetings" className="btn-secondary">{dv.dashboard.viewAll}</Link>
          </div>
        </section>
      )}

      {/* Stats infographics, side-by-side on desktop:
          - Leaderboard: who's proposed the most approved agenda items this year.
          - Thanfeezu: body-wide decision-implementation rate + status breakdown.
          Both body-aware, defaulting to the viewer's own body. */}
      <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-8">
        <Leaderboard />
        <ThanfeezuCard />
      </div>

      {/* Meeting lists — what's coming up and what's on record, stacked. */}
      <div className="space-y-6">
        <section>
          <h2 className="section-heading">
            <span className="text-lg font-bold">{dv.dashboard.upcomingMeetings}</span>
            {rest.length > 0 && <span className="pill pill-active shrink-0">{rest.length}</span>}
          </h2>
          <div className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
            {rest.map((m) => (
              <MeetingListRow key={m.id} meeting={m} />
            ))}
            {rest.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">{dv.meetings.empty}</p>
            )}
          </div>
          <div className="mt-3 text-start">
            <Link to="/meetings" className="btn-secondary btn-sm inline-flex items-center gap-1">
              {dv.dashboard.viewAll}
              <ChevronLeft size={15} strokeWidth={1.8} aria-hidden />
            </Link>
          </div>
        </section>

        <section>
          <h2 className="section-heading">
            <span className="text-lg font-bold">{dv.dashboard.pastMeetings}</span>
            {pastList.length > 0 && <span className="pill pill-neutral shrink-0">{pastList.length}</span>}
          </h2>
          <div className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
            {pastList.map((m) => (
              <MeetingListRow key={m.id} meeting={m} />
            ))}
            {pastList.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">{dv.meetings.empty}</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
